import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Shield, Database, Settings, Lock, UserCheck, MapPin, Cookie } from 'lucide-react';
import { useCookieConsentContext } from '@/contexts/CookieConsentContext';

export default function Privacy() {
  const { openPreferences } = useCookieConsentContext();
  
  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Privacy Policy', href: '/privacy' }
  ];

  const sections = [
    {
      icon: Shield,
      title: 'Introduction',
      content: 'SmartSite Solutions ("we", "us", or "our") is committed to protecting your privacy. This policy details how we collect, use, and safeguard your personal information in compliance with UK GDPR.'
    },
    {
      icon: Cookie,
      title: 'Cookies & Consent',
      content: 'We only use cookies with your explicit consent. Essential cookies are required for basic site functionality and cannot be disabled. Analytics cookies (via Google Analytics) are optional and help us understand how visitors use our site. All analytics data is anonymised and used solely to measure and improve site performance. You can withdraw your consent at any time via the Cookie Settings button below or in the footer.'
    },
    {
      icon: Database,
      title: 'Data We Collect',
      content: 'We collect information you provide directly to us via our contact forms, including your Name, Business Name, Email Address, and Phone Number. We also collect anonymous usage data to improve site performance (Core Web Vitals).'
    },
    {
      icon: Settings,
      title: 'How We Use Data',
      content: 'To provide quotes, deliver web design services, and manage monthly Care Plans. We never sell your data to third parties.'
    },
    {
      icon: Lock,
      title: 'Data Storage',
      content: 'Your data is stored securely on encrypted servers. We retain project data for the duration of our contract and for tax compliance purposes.'
    },
    {
      icon: UserCheck,
      title: 'Your Rights',
      content: 'Under UK law, you have the right to request access to, correction of, or deletion of your personal data. Contact us at smartsitesolutions@pm.me to exercise these rights.'
    },
    {
      icon: MapPin,
      title: 'Contact',
      content: 'SmartSite Solutions, North Cornelly, South Wales.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Helmet>
        <title>Privacy Policy | SmartSite Solutions</title>
        <meta name="description" content="SmartSite Solutions Privacy Policy. Learn how we collect, use, and safeguard your personal information in compliance with UK GDPR." />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/privacy" />
      </Helmet>
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Breadcrumbs */}
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs items={breadcrumbItems} />
          </div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto mb-12"
          >
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-white">
              Privacy Policy.
            </h1>
            <p className="text-lg text-slate-400">
              Last Updated: January 2026.
            </p>
          </motion.div>

          {/* Glassmorphism Container */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative bg-white/5 backdrop-blur-xl border border-slate-700 rounded-3xl p-8 lg:p-12 overflow-hidden">
              {/* Subtle background glows */}
              <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-32 -left-32 w-64 h-64 rounded-full bg-orange-500/10 blur-3xl pointer-events-none" />
              
              <div className="relative space-y-10">
                {sections.map((section, index) => (
                  <motion.section
                    key={section.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 flex items-center justify-center">
                        <section.icon className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <h2 className="font-display text-xl lg:text-2xl font-bold text-white mb-3">
                          {section.title}
                        </h2>
                        <p className="text-slate-300 leading-relaxed">
                          {section.content}
                        </p>
                      </div>
                    </div>
                    {index < sections.length - 1 && (
                      <div className="mt-10 border-b border-slate-700/50" />
                    )}
                  </motion.section>
                ))}
                
                {/* Cookie Settings Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + sections.length * 0.1 }}
                  className="pt-6 mt-6 border-t border-slate-700/50"
                >
                  <Button
                    onClick={openPreferences}
                    className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-lg shadow-orange-500/25"
                  >
                    <Cookie className="w-4 h-4 mr-2" />
                    Manage Cookie Preferences
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
